import Image, { ImageProps } from 'next/image';
import { Skeleton } from '@mui/material';
import { StaticImport } from 'next/dist/shared/lib/get-img-props';

type SafeImagePropsBase = Omit<ImageProps, 'src' | 'alt'> & {
    src?: string | StaticImport | null;
    alt?: string;
    fallbackSource?: string;
};

type SafeImagePropsWithWidth = SafeImagePropsBase & {
    width: number;
    height?: never;
};

type SafeImagePropsWithHeight = SafeImagePropsBase & {
    width?: never;
    height: number;
};

type SafeImagePropsWithBoth = SafeImagePropsBase & {
    width: number;
    height: number;
};

// This type means that you can pass either width or height or both to the SafeImage component
export type SafeImageProps = SafeImagePropsWithBoth | SafeImagePropsWithHeight | SafeImagePropsWithWidth;

export default function SafeImage(props: SafeImageProps) {
    const { src, fallbackSource, height, width, alt, sizes, ...imageProps } = props;

    const heightOrWidth = height || width;
    const widthOrHeight = width || height;

    if (!src || alt === undefined) {
        if (fallbackSource && alt) {
            return (
                <Image
                    priority={false}
                    src={fallbackSource}
                    height={heightOrWidth}
                    width={widthOrHeight}
                    alt={alt}
                    {...imageProps}
                />
            );
        }

        return (
            <Skeleton width={widthOrHeight} height={heightOrWidth} />
        );
    }

    const finalWidth = height ? `${width}px` : 'auto';
    const finalHeight = width ? `${height}px` : 'auto';

    return (
        <Image
            src={src as string}
            style={{ width: finalWidth, height: finalHeight }}
            // The 0 is to prevent next.js from complaining about it being "unmodified"
            // The size comes from the style as well as the "sizes" attribute
            height={height || 0}
            width={width || 0}
            // sizes can be used instead of width and height for dimensions next.js renders at
            sizes={sizes ?? `${widthOrHeight}px`}
            alt={alt}
            priority={false}
            {...imageProps}
        />
    );
}
